import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useUser } from './WebSocketContext';
import AddProject from './AddProject';
import ProtectedRoute from './ProtectedRoute';
import WorkspaceHome from './WorkspaceHome';
import useNavigationStorage from '../hooks/useNavigationStorage';
import { apiRequest } from '../Utils/apiClient';
import { normalizeResumePath, readNavigationItem, writeNavigationItem } from '../Utils/navigationMemory';

export function RememberProjectRoute() {
  const location = useLocation();
  const storage = useNavigationStorage();
  useEffect(() => {
    const queue = new URLSearchParams(location.search).get('queue');
    const path = normalizeResumePath(location.pathname + (
      location.pathname.startsWith('/validate/') && ['split', 'normal'].includes(queue)
        ? `?queue=${queue}` : ''
    ));
    if (path) {
      writeNavigationItem(storage, 'lastProject', path);
      writeNavigationItem(storage, 'templateIntroductionSeen', 'true');
    }
  }, [storage, location.pathname, location.search]);
  return null;
}

export function WorkspaceHomeRoute() {
  const { permissions, isReadOnly, isValidator } = useUser();
  if (!permissions || isValidator || isReadOnly) return <ResumeHomeRoute />;
  return <ProtectedRoute><WorkspaceHome /></ProtectedRoute>;
}

export function ResumeHomeRoute() {
  const { permissions, isReadOnly, isValidator } = useUser();
  const storage = useNavigationStorage();
  const [destination, setDestination] = useState(null);

  useEffect(() => {
    if (!permissions) return undefined;
    let active = true;
    setDestination(null);
    const finish = result => {
      if (active) setDestination({ ...result, storage });
    };
    const resolve = async () => {
      const saved = normalizeResumePath(readNavigationItem(storage, 'lastProject'));
      if (saved) {
        try {
          const result = await apiRequest(`/api/v1/projects/${saved.split(/[/?]/)[2]}`);
          if (result.status === 'success') {
            finish({ path: isValidator
              ? saved.replace('/case/', '/validate/')
              : saved.replace('/validate/', '/case/').split('?')[0] });
            return;
          }
        } catch (error) {
          if (![403, 404].includes(error.status)) throw error;
        }
        writeNavigationItem(storage, 'lastProject', '');
      }
      if (isValidator || isReadOnly) {
        finish({ path: isValidator ? '/validate' : '/project-dashboard' });
        return;
      }
      // Use the existing paginated, permission-filtered list for users without browser memory.
      const result = await apiRequest('/api/v1/sidebar/projects?page=1');
      if (!Array.isArray(result?.cases) || (result.status !== undefined && result.status !== 'success')) {
        throw new Error('Project list is unavailable');
      }
      const project = result.cases?.[0];
      if (project) {
        finish({ path: `/case/${project.id}` });
      } else {
        const introduce = !readNavigationItem(storage, 'templateIntroductionSeen');
        if (active) {
          writeNavigationItem(storage, 'templateIntroductionSeen', 'true');
          finish({ introduce });
        }
      }
    };
    resolve().catch(error => {
      console.error('Unable to restore the last project:', error);
      finish({ path: isValidator ? '/validate' : isReadOnly ? '/project-dashboard' : undefined, introduce: false });
    });
    return () => { active = false; };
  }, [permissions, storage, isReadOnly, isValidator]);

  if (!destination || destination.storage !== storage) return null;
  if (destination.path) return <Navigate to={destination.path} replace />;
  return <ProtectedRoute><AddProject autoOpenTemplates={destination.introduce} /></ProtectedRoute>;
}
